import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatNumber } from "../lib/api";
import { Plus, Trash2, Truck, Loader2, X } from "lucide-react";

export default function SuppliersList() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setSuppliers(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post("/suppliers", { name: newName });
      setShowNew(false); setNewName("");
      load();
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este proveedor y su catálogo?")) return;
    await api.delete(`/suppliers/${id}`);
    load();
  };

  return (
    <div className="p-8 fade-in" data-testid="suppliers-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Proveedores</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1">Catálogos de laboratorios</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Sube el catálogo de un laboratorio (ej. Sandoz, Cinfa, Normon) y cruza datos contra cualquier estudio para detectar productos faltantes, oportunidades de conversión de genéricos y generar el pedido de reposición automático.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn btn-primary" data-testid="new-supplier-button">
          <Plus size={16} /> Nuevo proveedor
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Cargando…</div>
      ) : suppliers.length === 0 ? (
        <div className="card p-12 text-center">
          <Truck size={36} className="mx-auto text-slate-400" />
          <h3 className="font-display text-xl font-bold mt-3">Aún no hay proveedores</h3>
          <p className="text-slate-600 mt-1 mb-4">Crea el primero (ej. "Sandoz") y luego sube su catálogo Excel.</p>
          <button onClick={() => setShowNew(true)} className="btn btn-primary"><Plus size={16} /> Crear proveedor</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((s) => (
            <Link key={s.id} to={`/suppliers/${s.id}`} className="card p-5 hover:shadow-md transition" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Proveedor</div>
                  <h3 className="font-display text-xl font-bold mt-1 truncate">{s.name}</h3>
                </div>
                <button onClick={(e) => { e.preventDefault(); del(s.id); }} className="text-red-600 hover:text-red-800 p-1" data-testid={`delete-supplier-${s.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">Productos</div>
                  <div className="font-display font-bold text-lg">{formatNumber(s.products_count)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">Actualizado</div>
                  <div className="text-xs font-mono">{s.last_uploaded ? new Date(s.last_uploaded).toLocaleDateString("es-ES") : "—"}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showNew && (
        <>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" onClick={() => setShowNew(false)} />
          <form onSubmit={create} className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-2xl w-full max-w-md p-6 fade-in">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-display text-xl font-bold">Nuevo proveedor</h3>
              <button type="button" onClick={() => setShowNew(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Nombre</label>
            <input
              data-testid="new-supplier-name"
              autoFocus required value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Ej. Sandoz" className="input mt-1"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="btn btn-outline btn-sm">Cancelar</button>
              <button type="submit" disabled={creating} className="btn btn-primary btn-sm" data-testid="create-supplier-submit">
                {creating ? <><Loader2 size={14} className="animate-spin" /> Creando…</> : "Crear"}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
